import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Copy, ChevronDown, Sparkles, Loader2 } from 'lucide-react';
import { automationAPI } from '../../services/api';
import AutomationRuleDialog from '../../components/Automation/AutomationRuleDialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Card, CardContent } from '../../components/ui/card';

type AutomationTemplate = {
  vertical: string;
  key: string;
  name: string;
  description?: string;
  trigger_event: string;
  conditions: any[];
  actions: any[];
};

/**
 * 2026-08-09: back to a table + modal (see AutomationRuleDialog.tsx) — the
 * brief editor-first/side-list layout had no way to back out to "see all
 * rules" without a close affordance. The rule editor itself (conditions,
 * actions, List/Flow canvas) is unchanged — only how you get to it.
 */
const AutomationRulesPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [rules, setRules] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const [templates, setTemplates] = useState<AutomationTemplate[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState<AutomationTemplate | null>(null);
  const [aiDraftDialogOpen, setAiDraftDialogOpen] = useState(false);
  const [aiDraftDescription, setAiDraftDescription] = useState('');
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiDraftError, setAiDraftError] = useState('');

  useEffect(() => {
    loadRules();
    automationAPI
      .getAutomationTemplates()
      .then((data: AutomationTemplate[]) => setTemplates(Array.isArray(data) ? data : []))
      .catch(() => setTemplates([]));
  }, []);

  // 2026-08-09: Digital Companion's "Build automation rule" button on a
  // category deep-links here as /automation?new_rule=1&category_id=<id> —
  // opens the same create-rule dialog a template prefills (see
  // handleCreateFromTemplate below), with a category_id condition already
  // in place instead of the admin having to hunt down and type the
  // category id themselves. Params cleared after consuming so a page
  // refresh doesn't reopen the dialog.
  useEffect(() => {
    if (searchParams.get('new_rule') !== '1') return;
    const categoryId = searchParams.get('category_id');
    if (!categoryId) return;
    handleCreateFromTemplate({
      vertical: 'From category',
      key: 'category-deep-link',
      name: '',
      trigger_event: 'ticket_created',
      conditions: [
        { field_name: 'category_id', operator: 'equal_to', expected_value: categoryId, group_operator: 'AND' },
      ],
      actions: [],
    });
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const loadRules = async () => {
    try {
      setLoading(true);
      const data = await automationAPI.getAutomationRules();
      setRules(Array.isArray(data) ? data : []);
    } catch (err: any) {
      setError('Failed to load automation rules');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (id: number, currentEnabled: boolean) => {
    try {
      await automationAPI.toggleAutomationRule(id, !currentEnabled);
      loadRules();
    } catch (err) {
      setError('Failed to toggle automation rule');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this automation rule?')) {
      return;
    }
    try {
      await automationAPI.deleteAutomationRule(id);
      loadRules();
    } catch (err) {
      setError('Failed to delete automation rule');
    }
  };

  const handleClone = async (id: number) => {
    try {
      await automationAPI.cloneAutomationRule(id);
      loadRules();
    } catch (err) {
      setError('Failed to clone automation rule');
    }
  };

  const getEventLabel = (eventName: string) => {
    const labels: Record<string, string> = {
      'conversation_created': 'Conversation Created',
      'conversation_updated': 'Conversation Updated',
      'message_created': 'Message Created',
      'conversation_opened': 'Conversation Opened',
      'ticket_created': 'Ticket Created',
      'ticket_status_changed': 'Ticket Status Changed',
      'ticket_assigned': 'Ticket Assigned',
      'ticket_priority_changed': 'Ticket Priority Changed',
      'sla_at_risk': 'SLA At Risk',
      'sla_breached': 'SLA Breached',
    };
    return labels[eventName] || eventName;
  };

  const handleCreate = () => {
    setEditingRuleId(null);
    setSelectedTemplate(null);
    setDialogOpen(true);
  };

  const handleCreateFromTemplate = (template: AutomationTemplate) => {
    setEditingRuleId(null);
    setSelectedTemplate(template);
    setDialogOpen(true);
  };

  // §3.6: the draft never saves anything itself — a successful draft opens
  // the exact same create-rule dialog a template prefills, `enabled: false`
  // until the admin reviews every condition/action and hits Save themselves.
  const handleAiDraftSubmit = async () => {
    if (!aiDraftDescription.trim()) return;
    setAiDrafting(true);
    setAiDraftError('');
    try {
      const rule = await automationAPI.draftAutomationRule(aiDraftDescription.trim());
      setAiDraftDialogOpen(false);
      setAiDraftDescription('');
      handleCreateFromTemplate({
        vertical: 'AI-drafted',
        key: 'ai-draft',
        name: rule.name,
        description: rule.description,
        trigger_event: rule.trigger_event,
        conditions: rule.conditions || [],
        actions: rule.actions || [],
      });
    } catch (err: any) {
      setAiDraftError(err?.response?.data?.detail || 'Could not draft a rule from that description.');
    } finally {
      setAiDrafting(false);
    }
  };

  const handleEdit = (id: number) => {
    setEditingRuleId(id);
    setSelectedTemplate(null);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingRuleId(null);
    setSelectedTemplate(null);
  };

  const handleDialogSave = () => {
    loadRules();
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">Automation Rules</h1>
        <div className="flex items-center gap-2">
          {templates.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline">
                  <Sparkles className="h-4 w-4 mr-2" />
                  Start from template
                  <ChevronDown className="h-3.5 w-3.5 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                {templates.map((t) => (
                  <DropdownMenuItem key={t.key} onClick={() => handleCreateFromTemplate(t)}>
                    <div className="flex flex-col">
                      <span>{t.name}</span>
                      {t.description && (
                        <span className="text-xs text-muted-foreground">{t.description}</span>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Button variant="outline" onClick={() => { setAiDraftError(''); setAiDraftDialogOpen(true); }}>
            <Sparkles className="h-4 w-4 mr-2" />
            Draft with AI
          </Button>
          <Button onClick={handleCreate}>
            <Plus className="h-4 w-4 mr-2" />
            Create Rule
          </Button>
        </div>
      </div>

      <Dialog open={aiDraftDialogOpen} onOpenChange={(v) => !aiDrafting && setAiDraftDialogOpen(v)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Draft a rule with AI</DialogTitle>
            <DialogDescription>
              Describe what you want in plain language. You'll review every condition and
              action before anything is saved — nothing activates automatically.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            rows={3}
            value={aiDraftDescription}
            onChange={(e) => setAiDraftDescription(e.target.value)}
            placeholder="e.g. Escalate stockout tickets to the regional manager if unresolved after 4 hours"
            disabled={aiDrafting}
          />
          {aiDraftError && <p className="text-sm text-destructive">{aiDraftError}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiDraftDialogOpen(false)} disabled={aiDrafting}>
              Cancel
            </Button>
            <Button onClick={handleAiDraftSubmit} disabled={aiDrafting || !aiDraftDescription.trim()}>
              {aiDrafting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              {aiDrafting ? 'Drafting…' : 'Draft rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {error && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center justify-between">
            {error}
            <button
              onClick={() => setError('')}
              className="ml-2 text-xs underline hover:no-underline"
            >
              Dismiss
            </button>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Conditions</TableHead>
                <TableHead>Actions</TableHead>
                <TableHead>Fired</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!loading && rules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No automation rules found. Create one to get started!
                  </TableCell>
                </TableRow>
              )}
              {!loading && rules.map((rule) => (
                <TableRow key={rule.id}>
                  <TableCell>
                    <p className="text-sm font-medium">{rule.name}</p>
                    {rule.description && (
                      <p className="text-xs text-muted-foreground mt-0.5">{rule.description}</p>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{getEventLabel(rule.trigger_event)}</Badge>
                  </TableCell>
                  <TableCell className="text-sm">{rule.conditions_count || 0}</TableCell>
                  <TableCell className="text-sm">{rule.actions_count || 0}</TableCell>
                  <TableCell className="text-sm tabular-nums text-muted-foreground">
                    {rule.total_executions || 0}×
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={rule.enabled}
                      onCheckedChange={() => handleToggleEnabled(rule.id, rule.enabled)}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(rule.id)}
                        title="Edit"
                        className="h-8 w-8"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleClone(rule.id)}
                        title="Clone"
                        className="h-8 w-8"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(rule.id)}
                        title="Delete"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AutomationRuleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        ruleId={editingRuleId}
        initialTemplate={selectedTemplate}
      />
    </div>
  );
};

export default AutomationRulesPage;
